import React from 'react'
import { Form, Radio, Input, Button, Alert, Row, Col, Skeleton, Divider, message } from 'antd'
import { FormComponent, BaseFormComponentProps } from '../../BaseComponent'
import { CopyOutlined } from '@ant-design/icons'
import config from '../../../config'

type TokenProps = BaseFormComponentProps & {
  uuid?: string,
  loading?: boolean,
  regeneration?: boolean,
  data: any | null,
  error: string | null,

  generatedUuid?: string | null,
  generatedPassword?: string | null,

  loadToken?: Function,

  dispatchSave: Function,
  dispatchReset: Function
}

export default class Token extends FormComponent<TokenProps> {

  static defaultProps = {
    loading: false,
    regeneration: true
  }

  state = {
    loaded: false,
    requested: false,
    generated: null as any,
    email: ''
  }

  static getDerivedStateFromProps(props: any, state: any) {
    if (!state.loaded && props.generatedUuid && props.generatedPassword) {
      if (!props.uuid || props.uuid === props.generatedUuid) {
        state.loaded = true
        state.generated = {
          uuid: props.generatedUuid,
          password: props.generatedPassword
        }

        return state
      }
    }

    // Do we have what is needed to make the request?
    if (!state.loaded && !state.requested && !props.loading && props.uuid && props.loadToken) {
      state.requested = true

      props.loadToken({
        search: {
          uuid: props.uuid
        }
      })

      return state
    }

    // Load state data
    if (!state.loaded && state.requested && !props.loading) {
      const { uuid, data } = props.data || {}

      state.loaded = true
      state.generated = {
        uuid: uuid || props.uuid,
        ...data || {}
      }
    }

    return state
  }

  componentDidMount() {
    this.validateAccessPermissions()
  }

  handleEmailChange = (ev: any) => {
    const { currentTarget: { value } } = ev

    this.setState({
      email: value
    })
  }

  handleCopy = (value: string | null | undefined) => {
    if (!value || value.length === 0) {
      return
    }

    if (!navigator.clipboard) {
      message.error('Failed to copy')
      return
    }

    navigator.clipboard.writeText(value)
    message.success('Copied to clipboard')
  }

  handleSave = (values: any) => {
    this.props.dispatchSave(values)
  }

  handleReset = () => {
    this.setState({
      loaded: false,
      requested: false,
      generated: null,
      email: ''
    }, () => {
      this.props.dispatchReset()
    })
  }

  renderSuccess = () => {
    const { regeneration } = this.props
    const { email, generated } = this.state
    const loginUrl = `${config.adoptBase}/?email=${generated?.uuid}&password=${generated?.password}`

    return (
      <>
        {
         email.length > 0 ? (
          <Alert message="Generated Token" description={`Token was generated for user e-mail: ${email}`} type="success" showIcon />
         ) : null
        }

        <Row style={{marginBottom: 10, marginTop: 10}}>
          <Col xs={18}>
            <Input value={generated?.uuid} size="large" readOnly disabled />
          </Col>

          <Col xs={6}>
            <Button type="ghost" size="large" onClick={() => this.handleCopy(generated?.uuid)}>
              <CopyOutlined />
              Copy
            </Button>
          </Col>
        </Row>

        {
          generated?.password ? (
            <>
              <Row style={{marginBottom: 10}}>
                <Col xs={18}>
                  <Input value={generated?.password} size="large" readOnly disabled />
                </Col>

                <Col xs={6}>
                  <Button type="ghost" size="large" onClick={() => this.handleCopy(generated?.password)}>
                    <CopyOutlined />
                    Copy
                  </Button>
                </Col>
              </Row>

              <Row style={{marginBottom: 10}}>
                <Col xs={18}>
                  <Input value={loginUrl} size="large" readOnly disabled />
                </Col>

                <Col xs={6}>
                  <Button type="ghost" size="large" onClick={() => this.handleCopy(loginUrl)}>
                    <CopyOutlined />
                    Copy
                  </Button>
                </Col>
              </Row>
            </>
          ) : (
            <Alert message="The password is shown only when the token is generated." type="info" showIcon style={{marginBottom: 10}} />
          )
        }

        {
          regeneration ? (
            <Button type="primary" htmlType="submit" size="large" onClick={this.handleReset} style={{marginTop: 30}} block>
              Generate Again
            </Button>
          ) : null
        }
      </>
    )
  }

  render() {
    const { uuid, error, loading } = this.props
    const { loaded, generated } = this.state

    // Loading details
    if (uuid && !loaded) {
      return <Skeleton />
    }

    if (uuid && generated) {
      return this.renderSuccess()
    }

    return (
      <Form
        name="token"
        layout="vertical"
        initialValues={{
          type: 'adopter'
        }}
        onFinish={this.handleSave}
      >
        {
          error ? (
            <>
              <Alert message="Failed to generate Token" description={error} type="error" showIcon />
              <Divider />
            </>
          ) : null
        }

        <Form.Item
          label="Project"
          name="type"
          rules={[
            {
              required: true,
              message: 'Missing a project'
            }
          ]}
        >
          <Radio.Group>
            <Radio value="adopter">Adopter</Radio>
            <Radio value="organization">Organization</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="User E-mail"
          name="user_email"
          rules={[
            {
              type: 'email',
              message: 'This is not a valid Email'
            },
            {
              required: true,
              message: 'Missing an email'
            }
          ]}
        >
          <Row>
            <Col xs={18}>
              <Input placeholder="User e-mail" size="large" onChange={this.handleEmailChange} />
            </Col>

            <Col xs={6}>
              <Button type="ghost" size="large" onClick={() => this.handleCopy(this.state.email)}>
                <CopyOutlined />
                Copy
              </Button>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item style={{marginTop: 32}}>
          <Button type="primary" htmlType="submit" loading={loading} size="large" ref={(refs) => this.actionRefs.save = refs} block>
            Generate
          </Button>
        </Form.Item>
      </Form>
    )
  }
}
